import React, {useState, useEffect, useCallback, useContext} from "react";
import './ProfileTabContent.css';
import {FaRegUserCircle} from "react-icons/fa";
import {authAPI} from '../../../Api/api';
import { useToast } from '../../../Context/ToastContext';
import {useTranslation} from "react-i18next";
import CustomDropdown from '../../../Component/UI/CustomDropdown/CustomDropdown';
import Button from '../../../Component/UI/Button/Button';
import LanguageContext from '../../../Context/LanguageContext';


const timezoneOptions = [
    { value: 'UTC', label: 'UTC' },
    { value: 'EST', label: 'EST' },
    { value: 'CST', label: 'CST' },
    { value: 'MST', label: 'MST' },
    { value: 'PST', label: 'PST' }
];

const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'tr', label: 'Türkçe' },
    { value: 'fa', label: 'فارسی' },
    { value: 'ps', label: 'پښتو' },
    { value: 'ar', label: 'العربية' },
    { value: 'ru', label: 'Русский' },
    { value: 'el', label: 'Ελληνικά' }
];

function ProfileTabContent() {
    const { dir } = useContext(LanguageContext);
    const { t } = useTranslation();
    const { addToast } = useToast();

    const [loading, setLoading] = useState(false);

    // User profileSummary state
    const [user, setUser] = useState({
        id: '',
        name: '',
        email: '',
        role: 'user',
        accountStatus: 'active',
        profile: {
            phone: '',
            avatar: '',
            timezone: 'UTC',
            language: 'en',
            bio: '',
            address: ''
        }
    });

    // Edit profileSummary form state
    const [editFormData, setEditFormData] = useState({
        name: '',
        phone: '',
        timezone: 'UTC',
        language: 'en',
        bio: '',
        address: ''
    });
    const [editErrors, setEditErrors] = useState({});

    // Avatar upload state
    const [uploadingAvatar, setUploadingAvatar] = useState(false);

    // ==================== USER DATA FETCH ====================
    const fetchUserData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await authAPI.getUserData();
            const userData = response.data;

            const newUserData = {
                id: userData._id || userData.id,
                name: userData.name,
                email: userData.email,
                role: userData.role,
                accountStatus: userData.accountStatus,
                profile: userData.profile || {
                    phone: '',
                    avatar: '',
                    timezone: 'UTC',
                    language: 'en',
                    bio: '',
                    address: ''
                }
            };

            setUser(newUserData);
            setEditFormData({
                name: newUserData.name,
                phone: newUserData.profile?.phone || '',
                timezone: newUserData.profile?.timezone || 'UTC',
                language: newUserData.profile?.language || 'en',
                bio: newUserData.profile?.bio || '',
                address: newUserData.profile?.address || ''
            });
        } catch (err) {
            console.error('Error fetching user data:', err);
            addToast(t('profilePage.profileLoadFailed'), 'error');
        } finally {
            setLoading(false);
        }
    }, [t, addToast]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            fetchUserData();
        }
    }, [fetchUserData]);


    // ==================== EDIT PROFILE ====================
    const handleEditFormChange = (e) => {
        const {name, value} = e.target;
        setEditFormData(prev => ({...prev, [name]: value}));
        if (editErrors[name]) {
            setEditErrors(prev => ({...prev, [name]: ''}));
        }
    };


    const validateEditForm = () => {
        const errors = {};
        if (!editFormData.name.trim()) {
            errors.name = t('profilePage.nameRequired');
        }
        return errors;
    };

    const handleEditProfileSubmit = async (e) => {
        e.preventDefault();
        const errors = validateEditForm();

        if (Object.keys(errors).length > 0) {
            setEditErrors(errors);
            return;
        }

        setLoading(true);
        try {
            const response = await authAPI.updateProfile(user.id, editFormData);

            setUser(prev => ({
                ...prev,
                name: editFormData.name,
                profile: {
                    ...prev.profile,
                    phone: editFormData.phone,
                    timezone: editFormData.timezone,
                    language: editFormData.language,
                    bio: editFormData.bio,
                    address: editFormData.address
                }
            }));

            addToast(response.data.message || t('profilePage.profileSuccess'), 'success');
        } catch (err) {
            addToast(err.response?.data?.message || t('profilePage.profileFailed'), 'error');
        } finally {
            setLoading(false);
        }
    };

    // ==================== AVATAR UPLOAD ====================
    const handleAvatarChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingAvatar(true);
        try {
            const response = await authAPI.updateAvatar(user.id, file);
            const newAvatar = response.data?.avatar;
            if (newAvatar) {
                setUser(prev => ({...prev, profile: {...prev.profile, avatar: newAvatar}}));
                addToast(t('profilePage.avatarUpdated'), 'success');
            }
        } catch (err) {
            console.error('Avatar upload failed', err);
            addToast(err.response?.data?.message || t('profilePage.avatarUploadFailed'), 'error');
        } finally {
            setUploadingAvatar(false);
            e.target.value = '';
        }
    };

    return (
        <>
            <div className='profile-summary'>
                {user.profile?.avatar ? (
                    <img src={user.profile.avatar} alt='Avatar' className='profile-summary-avatar-img'/>
                ) : (
                    <FaRegUserCircle className='profile-summary-avatar'/>
                )}
                <h3 className='profile-summary-name'>{user.name || '-'}</h3>
                <p className='profile-summary-email'>{user.email || '-'}</p>
                <label className='profile-avatar-upload'>
                    <input
                        type='file'
                        accept='image/*'
                        onChange={handleAvatarChange}
                        disabled={uploadingAvatar || loading}
                        style={{display: 'none'}}
                    />
                    <span className='profile-avatar-upload-btn'>
                        {uploadingAvatar ? t('profilePage.uploading') : t('profilePage.changeAvatar')}
                    </span>
                </label>
            </div>

            <div
                className='profile-details'
                dir={dir()}
            >
                <div className='profile-details-item'>
                    <strong>{t('profilePage.status')}:</strong>
                    <span>{user.accountStatus}</span>
                </div>
            </div>

            <form
                className='profile-form'
                onSubmit={handleEditProfileSubmit}
                noValidate
                dir={dir()}
            >
                <h2 className='profile-form-title'>{t('profilePage.editProfileTitle')}</h2>

                <div className='profile-form-content'>
                    {/* First Row: Name and Email */}
                    <div className='profile-form-row'>
                        {/* Name Field */}
                        <div className='profile-form-box'>
                            <label className='profile-form-label'>{t('profilePage.name')}</label>
                            <input
                                className={`profile-form-input ${editErrors.name ? 'profile-form-input-error' : ''}`}
                                type='text'
                                name='name'
                                value={editFormData.name}
                                onChange={handleEditFormChange}
                                placeholder={t('profilePage.name')}
                                aria-invalid={!!editErrors.name}
                                aria-describedby={editErrors.name ? 'name-error' : undefined}
                            />
                            {editErrors.name && (
                                <span id='name-error' className='profile-form-error' role='alert'>
                            {editErrors.name}
                          </span>
                            )}
                        </div>

                        {/* Email Field - Read Only */}
                        <div className='profile-form-box'>
                            <label className='profile-form-label'>{t('profilePage.email')}</label>
                            <input
                                className='profile-form-input profile-form-input-disabled'
                                type='email'
                                value={user.email}
                                disabled
                                placeholder={t('profilePage.email')}
                            />
                        </div>
                    </div>

                    {/* Second Row: Timezone and Language */}
                    <div className='profile-form-row'>
                        {/* Timezone Field */}
                        <div className='profile-form-box'>
                            <CustomDropdown
                                options={timezoneOptions}
                                value={editFormData.timezone}
                                onChange={(value) => handleEditFormChange({target: {name: 'timezone', value}})}
                                placeholder={t('profilePage.selectTimezone')}
                                label={t('profilePage.timezone')}
                            />
                        </div>

                        {/* Language Field */}
                        <div className='profile-form-box'>
                            <CustomDropdown
                                options={languageOptions}
                                value={editFormData.language}
                                onChange={(value) => handleEditFormChange({target: {name: 'language', value}})}
                                placeholder={t('profilePage.selectLanguage')}
                                label={t('profilePage.language')}
                            />
                        </div>
                    </div>

                    {/* Third Row: Phone and Address */}
                    <div className='profile-form-row'>
                        {/* Phone Field */}
                        <div className='profile-form-box'>
                            <label className='profile-form-label'>{t('profilePage.phone')}</label>
                            <input
                                className='profile-form-input'
                                type='text'
                                name='phone'
                                value={editFormData.phone}
                                onChange={handleEditFormChange}
                                placeholder={t('profilePage.phone')}
                            />
                        </div>

                        {/* Address Field */}
                        <div className='profile-form-box'>
                            <label className='profile-form-label'>{t('profilePage.address')}</label>
                            <input
                                className='profile-form-input'
                                type='text'
                                name='address'
                                value={editFormData.address}
                                onChange={handleEditFormChange}
                                placeholder={t('profilePage.address')}
                            />
                        </div>
                    </div>

                    {/* Bio Field */}
                    <div className='profile-form-box'>
                        <label className='profile-form-label'>{t('profilePage.bio')}</label>
                        <textarea
                            name='bio'
                            value={editFormData.bio}
                            onChange={handleEditFormChange}
                            placeholder={t('profilePage.bio')}
                            className='profile-form-input'
                            rows='4'
                        />
                    </div>
                </div>

                <div className='profile-actions'>
                    <Button
                        className='profile-button'
                        type='submit'
                        disabled={loading}
                        aria-busy={loading}
                    >
                        {loading ? t('profilePage.saving') : t('profilePage.save')}
                    </Button>
                </div>
            </form>
        </>
    );
}

export default ProfileTabContent;

